import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, BarChart3, Users, Settings, LogOut, Menu, X, Inbox, ExternalLink, Search, Share2, Store, ShieldCheck, ChevronLeft, ChevronRight, Map, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useUpgrade } from '@/hooks/useUpgrade';

const API = '/api';

const DashboardSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar_collapsed', String(next));
  };
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isPro = user?.subscription?.tier === 'pro';
  const userSlug = user?.slug;
  const { upgrading, handleUpgrade } = useUpgrade();

  const [browseEnabled, setBrowseEnabled] = useState(false);

  const handleViewBesume = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.get(`${API}/profile/me/data`);
      if (res.data?.is_published) {
        window.open(`/${userSlug}`, '_blank');
      } else {
        toast('Complete your interview first to publish your Besume.');
        navigate('/interview');
      }
    } catch {
      toast('Complete your interview first to publish your Besume.');
      navigate('/interview');
    }
  };

  useEffect(() => {
    axios.get(`${API}/browse/enabled`)
      .then(res => setBrowseEnabled(res.data.enabled))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/contact/messages/unread-count`)
      .then(res => setUnreadMessages(res.data.unread || 0))
      .catch(() => {});
  }, [location.pathname]);
  
  // Top scrollable — Group 1: Core
  const primaryNavItems = [
    { path: '/dashboard/profile', icon: LayoutDashboard, label: 'Besume' },
    { path: '/dashboard/sell', icon: Store, label: 'Sell' },
    { path: '/dashboard/network', icon: Users, label: 'Network' },
  ];

  // Top scrollable — Group 2: Distribution
  const secondaryNavItems = [
    { path: '/dashboard/share', icon: Share2, label: 'Share' },
    { path: '/dashboard/inbox', icon: Inbox, label: 'Inbox' },
  ];

  // Top scrollable — Group 3: Growth / Insights
  const tertiaryNavItems = [
    { path: '/roadmap', icon: Map, label: 'Roadmap' },
    { path: '/dashboard/analytics', icon: BarChart3, label: 'Analytics', pro: true },
  ];
  
  const isActive = (path) => {
    if (path === '/dashboard/profile') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/profile';
    }
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ item, onClick }) => {
    const isProLocked = item.pro && !isPro;
    const handleClick = (e) => {
      if (isProLocked) {
        e.preventDefault();
        toast('Analytics is a Pro feature. Upgrade to Pro to access.');
        return;
      }
      if (onClick) onClick(e);
    };
    return (
      <Link 
        to={isProLocked ? '#' : item.path} 
        onClick={handleClick}
        className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'} text-sm uppercase tracking-widest font-medium transition-colors ${
          isProLocked
            ? 'text-muted-foreground/40 cursor-not-allowed'
            : isActive(item.path) 
              ? 'bg-secondary text-foreground' 
              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
        title={collapsed ? item.label : undefined}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
        {!collapsed && (
          <>
            {item.label}
            {item.path === '/dashboard/inbox' && unreadMessages > 0 && (
              <span className="ml-auto w-2 h-2 rounded-full bg-foreground" data-testid="unread-badge" />
            )}
            {isProLocked && (
              <span className="text-[10px] border border-border px-1.5 py-0.5 ml-auto">PRO</span>
            )}
          </>
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Desktop Sidebar - visible on lg and above */}
      <aside className={`hidden lg:flex fixed left-0 top-0 bottom-0 border-r border-border bg-background flex-col z-40 transition-all duration-200 ${collapsed ? 'w-16' : 'w-64'}`}>
        {/* Header (logo + search) — fixed at top */}
        <div className={`flex-shrink-0 ${collapsed ? 'px-2 pt-6' : 'px-6 pt-6'}`}>
          <Link to="/" className={`block text-xl font-serif font-black tracking-tighter mb-8 ${collapsed ? 'text-center' : ''}`}>
            {collapsed ? 'b' : 'besume'}
          </Link>

          {!collapsed && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = e.target.elements.q.value.trim();
                if (q) {
                  navigate(`/search/products?q=${encodeURIComponent(q)}`);
                  e.target.elements.q.value = '';
                }
              }}
              className="mb-6 px-2"
              data-testid="sidebar-search-form"
            >
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[#999] absolute left-2 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                <input
                  type="text"
                  name="q"
                  placeholder="Search products..."
                  className="w-full text-xs border border-[#e0e0e0] rounded-none py-1.5 pl-7 pr-2 focus:outline-none focus:border-[#0a0a0a] bg-white"
                  data-testid="sidebar-search-input"
                />
              </div>
            </form>
          )}
        </div>

        {/* Primary nav — scrollable */}
        <nav
          className={`flex-1 overflow-y-auto space-y-2 ${collapsed ? 'px-2' : 'px-6'}`}
          data-testid="sidebar-primary-nav"
        >
          {primaryNavItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
          
          <div className="border-t border-border my-4" />
          
          {secondaryNavItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
          
          <div className="border-t border-border my-4" />
          
          {tertiaryNavItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>

        {/* Bottom pinned group: Settings, View Besume, Admin, Browse, Upgrade CTA (free only), Logout */}
        <div
          className={`flex-shrink-0 border-t border-border pt-3 pb-4 ${collapsed ? 'px-2' : 'px-6'}`}
          data-testid="sidebar-bottom-pinned"
        >
          <NavLink item={{ path: '/settings', icon: Settings, label: 'Settings' }} />

          {/* View My Besume Link */}
          {userSlug && (
            <a 
              href={`/${userSlug}`}
              onClick={handleViewBesume}
              className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'} hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors`}
              title={collapsed ? 'View My Besume' : undefined}
              data-testid="view-my-besume-btn"
            >
              <ExternalLink className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && 'View My Besume'}
            </a>
          )}

          {/* Admin Link — admin users only */}
          {user?.is_admin && (
            <Link
              to="/admin"
              className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'} hover:bg-secondary text-sm uppercase tracking-widest font-medium transition-colors ${
                location.pathname === '/admin'
                  ? 'bg-secondary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title={collapsed ? 'Admin' : undefined}
              data-testid="admin-link"
            >
              <ShieldCheck className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && 'Admin'}
            </Link>
          )}

          {/* Browse Link — admin controlled */}
          {browseEnabled && (
            <Link 
              to="/browse"
              className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'} hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors`}
              title={collapsed ? 'Browse' : undefined}
              data-testid="browse-link"
            >
              <Search className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && 'Browse'}
            </Link>
          )}

          {/* Upgrade to Pro — free users only. Tighter vertical padding so the black block doesn't overpower the pinned section. */}
          {!isPro && (
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              title={collapsed ? 'Upgrade to Pro' : undefined}
              className={`flex items-center ${collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-4 py-2'} bg-foreground text-background text-sm uppercase tracking-widest font-medium hover:bg-foreground/90 transition-colors w-full disabled:opacity-50 my-1`}
              data-testid={collapsed ? 'sidebar-upgrade-pro-collapsed' : 'sidebar-upgrade-pro-btn'}
            >
              <Sparkles className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && (upgrading ? 'Redirecting…' : 'Upgrade to Pro')}
            </button>
          )}

          <button 
            onClick={onLogout}
            className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'} hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors w-full`}
            title={collapsed ? 'Logout' : undefined}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            {!collapsed && 'Logout'}
          </button>

          {/* Collapse toggle */}
          <div className="border-t border-border mt-2 pt-2 flex justify-center">
            <button
              onClick={toggleCollapsed}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              data-testid="sidebar-collapse-btn"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header Bar - visible below lg */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 border-b border-border bg-background flex items-center justify-between px-4 z-50">
        <Link to="/" className="text-xl font-serif font-black tracking-tighter">
          besume
        </Link>
        <button 
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 hover:bg-secondary rounded"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background">
          <div className="flex flex-col h-full">
            {/* Mobile header */}
            <div className="flex-shrink-0 flex items-center justify-between p-6 pb-4">
              <Link 
                to="/" 
                className="text-xl font-serif font-black tracking-tighter"
                onClick={() => setMobileMenuOpen(false)}
              >
                besume
              </Link>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-secondary rounded"
                aria-label="Close menu"
                data-testid="close-mobile-menu-btn"
              >
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            {/* Mobile scrollable nav */}
            <nav className="flex-1 overflow-y-auto px-6 space-y-2" data-testid="mobile-primary-nav">
              {primaryNavItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  onClick={() => setMobileMenuOpen(false)} 
                />
              ))}
              
              <div className="border-t border-border my-4" />
              
              {secondaryNavItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  onClick={() => setMobileMenuOpen(false)} 
                />
              ))}
              
              <div className="border-t border-border my-4" />
              
              {tertiaryNavItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  onClick={() => setMobileMenuOpen(false)} 
                />
              ))}
            </nav>

            {/* Mobile bottom pinned group */}
            <div className="flex-shrink-0 border-t border-border pt-3 pb-6 px-6" data-testid="mobile-bottom-pinned">
              <NavLink 
                item={{ path: '/settings', icon: Settings, label: 'Settings' }} 
                onClick={() => setMobileMenuOpen(false)} 
              />

              {/* View My Besume Link */}
              {userSlug && (
                <a 
                  href={`/${userSlug}`}
                  onClick={(e) => { handleViewBesume(e); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  View My Besume
                </a>
              )}

              {/* Admin Link — admin users only */}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-secondary text-sm uppercase tracking-widest font-medium transition-colors ${
                    location.pathname === '/admin'
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="mobile-admin-link"
                >
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                  Admin
                </Link>
              )}

              {/* Browse Link — admin controlled */}
              {browseEnabled && (
                <Link 
                  to="/browse"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Search className="w-4 h-4" strokeWidth={1.5} />
                  Browse
                </Link>
              )}

              {/* Upgrade to Pro — free users only. Tighter vertical padding. */}
              {!isPro && (
                <button
                  onClick={() => { setMobileMenuOpen(false); handleUpgrade(); }}
                  disabled={upgrading}
                  className="flex items-center gap-3 px-4 py-2 my-1 bg-foreground text-background text-sm uppercase tracking-widest font-medium hover:bg-foreground/90 transition-colors w-full disabled:opacity-50"
                  data-testid="mobile-upgrade-pro-btn"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  {upgrading ? 'Redirecting…' : 'Upgrade to Pro'}
                </button>
              )}

              <button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogout();
                }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary text-sm uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardSidebar;
